let youtubeApiCbs = []
let loadingYoutubeApi = false

let vimeoApiCbs = []
let loadingVimeoApi = false

let activeVideos = []

export function withYoutubeApi(cb) {
    if (window.YT) {
        cb(window.YT)
    } else {
        youtubeApiCbs.push(cb)

        if (!loadingYoutubeApi) {
            loadingYoutubeApi = true

            let prevFn = null
            if (window.onYouTubeIframeAPIReady) {
                prevFn = window.onYouTubeIframeAPIReady
            }
            window.onYouTubeIframeAPIReady = function() {
                if (typeof prevFn === 'function') {
                    prevFn()
                }
                youtubeApiCbs = youtubeApiCbs.filter(prevCb => {
                    withYoutubeApi(prevCb)
                    return false
                })
                window.onYouTubeIframeAPIReady = null
            }
            let scriptEl = document.createElement('script')
            scriptEl.src = 'https://www.youtube.com/iframe_api'
            scriptEl.async = true
            document.head.appendChild(scriptEl)
        }
    }
}

export function withVimeoApi(cb) {
    if (window.Vimeo) {
        cb(window.Vimeo)
    } else {
        vimeoApiCbs.push(cb)
        if (!loadingVimeoApi) {
            loadingVimeoApi = true

            let scriptEl = document.createElement('script')
            scriptEl.src = 'https://player.vimeo.com/api/player.js'
            scriptEl.async = true
            scriptEl.addEventListener('load', () => {
                vimeoApiCbs = vimeoApiCbs.filter(prevCb => {
                    withVimeoApi(prevCb)
                    return false
                })
            })
            document.head.appendChild(scriptEl)
        }
    }
}

export async function playVideo(htmlEl, options = {}) {
    return new Promise((resolve, reject) => {
        const { videoId, provider, background } = Object.assign({}, {
            videoId: '',
            provider: '',
            background: false,
        }, options)

        let html = null
        switch (provider) {
            case 'youtube':
                html = `<iframe src="https://www.youtube.com/embed/${videoId}?${background ? `modestbranding=1&mute=1&showinfo=0&loop=1&autohide=1&playsinline=1&playlist=${videoId}&` : ''}feature=oembed&autoplay=1&controls=${background ? '0' : 1}&fs=1&enablejsapi=1&rel=0" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`
                break;
            case 'vimeo':
                html = `<iframe src="https://player.vimeo.com/video/${videoId}?${background ? 'loop=1&autopause=0&background=1&muted=1&' : ''}autoplay=1&autopause=1" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`
                break;
            default:
                reject(`Video provider '${provider}' not supported`)
        }
        htmlEl.innerHTML = html
        const iframe = htmlEl.querySelector('iframe')
        let control
        switch (provider) {
            case 'youtube':
                withYoutubeApi(yt => {
                    const player = new yt.Player(iframe, {
                        events: {
                            onReady: () => {
                                player.playVideo()
                                htmlEl.classList.add('player--ready')
                                resolve(control)
                            }
                        }
                    })
                    control = {
                        get player() { return player },
                        pause() { return player.pauseVideo() },
                        play() { return player.playVideo() },
                        async isPlaying() { return player.getPlayerState() === 1 || player.getPlayerState() === 3 },
                        onPlayStateChange(cb) {
                            player.addEventListener('stateChange', event => {
                                switch (event.data) {
                                    case 0:
                                        cb('ended')
                                        break
                                    case 1:
                                        cb('playing')
                                        break
                                    case 2:
                                        cb('paused')
                                        break
                                    case 3:
                                        cb('buffering')
                                        break
                                }
                            })
                        },
                    }
                    activeVideos.push(control)
                })
                break
            case 'vimeo':
                withVimeoApi(vimeo => {
                    let player = new vimeo.Player(iframe)
                    player.on('loaded', () => {
                        htmlEl.classList.add('player--ready')
                        resolve(control)
                    })
                    control = {
                        get player() { return player },
                        pause() { return player.pause() },
                        play() { return player.play() },
                        async isPlaying() { return !await player.getPaused() },
                        onPlayStateChange(cb) {
                            player.on('play', () => cb('playing'))
                            player.on('pause', () => cb('paused'))
                            player.on('ended', () => cb('ended'))
                            player.on('bufferstart', () => cb('buffering'))
                        },
                    }
                    activeVideos.push(control)
                })
        }

        if (background) {
            const observer = new IntersectionObserver(entries => entries.forEach(entry => {
                if (control) {
                    if (entry.isIntersecting) {
                        control.play()
                    } else {
                        control.pause()
                    }
                }
            }))

            observer.observe(htmlEl)
        }
    })
}

const observerCbs = [];
let mainMtObserver = null;

/**
 *
 * @param {string|{root: HTMLElement, selector: string}} selector  CSS selector for statically rendered elements
 * @param {(el: HTMLElement, idx: number, els: HTMLElement[]) => any} initEachFn  Callback for initialization. Is given the same arguments as Array.prototype.map and Array.prototype.forEach functions
 * @param args Advanced configuration
 * @param {boolean} args.observe Either to observe dynamically added elements
 * @param {(el: HTMLElement) => void} args.observerFilterFn Function that tests inserted elements for initialization
 *
 * @returns {unknown[]} Array of whatever your initEachFn function returns
 */
export function initElements(selector, initEachFn, args = {}) {
    args = Object.assign({}, {
        observe: false,
        observerFilterFn: null,
    }, args);

    const root = typeof selector === "object" ? selector.root : document;
    const cssSelector = typeof selector === "object" ? selector.selector : selector;

    if (mainMtObserver && args.observe && typeof args.observerFilterFn === "function") {
        maybeInitMainMutationObserver();
        observerCbs.push((mutation => {
            [].slice.apply(mutation.nodeList).filter(args.observerFilterFn).forEach(initEachFn);
        }));
    }

    return [].slice.apply(root.querySelectorAll(cssSelector)).map(initEachFn);
}

function maybeInitMainMutationObserver() {
    if (!mainMtObserver && "MutationObserver" in  window) {
        mainMtObserver = new MutationObserver(mutations => mutations.forEach(mutation => {
            observerCbs.forEach(cb => cb(mutation));
        }));
        mainMtObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }
}
